import { STAGES, FINAL_STAGE } from "./evolution.ts";

// The DOM overlay: realm + ladder, karma score, the Three-Poison read-outs
// (mindfulness multiplier, fullness, breath cadence), and the full-screen
// start / reborn / enlightenment panels. Styled to match the thangka look.
export class Hud {
  root: HTMLElement;
  private stageLabel!: HTMLElement;
  private dots!: HTMLElement;
  private scoreEl!: HTMLElement;
  private muteBtn!: HTMLButtonElement;
  private mult!: HTMLElement;
  private clarityEl!: HTMLElement;
  private fullFill!: HTMLElement;
  private fullWrap!: HTMLElement;
  private enlFill!: HTMLElement;
  private breath!: HTMLElement;
  private warn!: HTMLElement;
  private overlay!: HTMLElement;

  onStart: () => void = () => {};
  onRestart: () => void = () => {};
  onToggleMute: () => boolean = () => false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
  }

  private build() {
    this.root.innerHTML = `
      <div class="hud-top">
        <div class="stage">
          <span class="stage-name">Single Cell</span>
          <div class="dots"></div>
        </div>
        <div class="top-right">
          <div class="score"><small>Karma</small><b>0</b></div>
          <button class="mute" aria-label="Toggle sound">🔊</button>
        </div>
      </div>

      <div class="mind">
        <span class="mult">×1.0</span>
        <span class="mind-lbl">mindful</span>
        <span class="clarity">☀ clarity</span>
      </div>

      <div class="warn">Full of filth — trace to cleanse</div>

      <div class="breath" aria-hidden="true"><span></span><em>breathe</em></div>

      <div class="meters">
        <div class="meter-row">
          <span class="lbl">Fullness</span>
          <div class="bar full"><i></i></div>
        </div>
        <div class="meter-row">
          <span class="lbl">Enlightenment</span>
          <div class="bar enl"><i></i></div>
        </div>
      </div>

      <div class="overlay"></div>
    `;
    this.stageLabel = this.q(".stage-name");
    this.dots = this.q(".dots");
    this.scoreEl = this.q(".score b");
    this.muteBtn = this.q(".mute");
    this.mult = this.q(".mult");
    this.clarityEl = this.q(".clarity");
    this.fullFill = this.q(".bar.full > i");
    this.fullWrap = this.q(".bar.full");
    this.enlFill = this.q(".bar.enl > i");
    this.breath = this.q(".breath");
    this.warn = this.q(".warn");
    this.overlay = this.q(".overlay");

    this.dots.innerHTML = STAGES.map(
      (s, i) => `<i class="dot${i === 0 ? " on" : ""}" title="${s.name}"></i>`,
    ).join("");

    this.muteBtn.addEventListener("click", () => {
      const muted = this.onToggleMute();
      this.muteBtn.textContent = muted ? "🔇" : "🔊";
    });

    this.showStart();
  }

  private q<T extends HTMLElement>(sel: string): T {
    return this.root.querySelector(sel) as T;
  }

  setStage(stage: number) {
    this.stageLabel.textContent = STAGES[Math.min(stage, FINAL_STAGE)].name;
    this.dots.querySelectorAll(".dot").forEach((d, i) => d.classList.toggle("on", i <= stage));
  }

  setMeter(fraction: number) {
    this.enlFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  }

  setFullness(fraction: number, overfull: boolean) {
    this.fullFill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
    this.fullWrap.classList.toggle("hot", overfull);
    this.warn.classList.toggle("on", overfull);
  }

  setMindfulness(m: number) {
    this.mult.textContent = `×${m.toFixed(1)}`;
    // grow + gild as the multiplier climbs
    const t = Math.min(1, (m - 1) / 3);
    this.mult.style.transform = `scale(${1 + t * 0.35})`;
    this.mult.style.color = m > 1.05 ? "#f4e2a1" : "#cdb87e";
  }

  setClarity(on: boolean) {
    this.clarityEl.classList.toggle("on", on);
  }

  setBreath(scale: number) {
    const s = 0.6 + scale * 0.7;
    (this.breath.firstElementChild as HTMLElement).style.transform = `scale(${s})`;
    this.breath.classList.toggle("peak", scale > 0.82);
  }

  setScore(n: number) {
    this.scoreEl.textContent = String(n);
  }

  private flash(cls: string) {
    const el = document.createElement("div");
    el.className = cls;
    this.root.appendChild(el);
    el.addEventListener("animationend", () => el.remove());
  }
  evolveFlash() {
    this.flash("evolve-flash");
  }
  goldenFlash() {
    this.flash("golden-flash");
  }

  banner(title: string, sub: string) {
    const el = document.createElement("div");
    el.className = "banner";
    el.innerHTML = `<div class="banner-title">${title}</div><div class="banner-sub">${sub}</div>`;
    this.root.appendChild(el);
    el.addEventListener("animationend", () => el.remove());
  }

  showStart() {
    this.overlay.className = "overlay show";
    this.overlay.innerHTML = `
      <div class="panel">
        <div class="logo">🧘💩</div>
        <h1>Pooping Buddha</h1>
        <p class="tag">Eat your way to enlightenment — mindfully.</p>
        <ul class="how">
          <li>👆 <b>Drag</b> to move · eat poop to evolve</li>
          <li>🍃 <b>Don't gorge.</b> Pace your bites on the breath for more karma</li>
          <li>🔀 <b>Don't cling.</b> Vary who you eat from — repetition pays less</li>
          <li>🌀 <b>Don't overfill.</b> When full, <b>tilt to trace the mandala</b> &amp; cleanse</li>
          <li>👹 <b>Bigger beings eat you.</b> Evolve to outgrow them — or get caught</li>
        </ul>
        <button class="btn play">Begin the Journey</button>
      </div>`;
    this.overlay.querySelector(".play")?.addEventListener("click", () => {
      this.hideOverlay();
      this.onStart();
    });
  }

  showGameOver(stageReached: string, score: number, hiscore: number) {
    this.overlay.className = "overlay show dead";
    this.overlay.innerHTML = `
      <div class="panel">
        <div class="logo">☠️</div>
        <h1>Devoured</h1>
        <p class="tag">Caught as a <b>${stageReached}</b>. Back to the wheel of samsara.</p>
        <div class="stats">
          <div><span>${score}</span>karma</div>
          <div><span>${hiscore}</span>best</div>
        </div>
        <button class="btn play">Be Reborn</button>
      </div>`;
    this.overlay.querySelector(".play")?.addEventListener("click", () => {
      this.hideOverlay();
      this.onRestart();
    });
  }

  showWin(score: number, hiscore: number, goldenEaten: number) {
    this.overlay.className = "overlay show win";
    this.overlay.innerHTML = `
      <div class="panel">
        <div class="logo glow">🧘</div>
        <h1>Enlightenment!</h1>
        <p class="tag">You became the <b>Buddha</b>. Now you poop gold for the world.</p>
        <div class="stats">
          <div><span>${score}</span>karma</div>
          <div><span>${hiscore}</span>best</div>
          <div><span>${goldenEaten}</span>golden</div>
        </div>
        <button class="btn share">Share ✨</button>
        <button class="btn play">Be Reborn</button>
      </div>`;
    this.overlay.querySelector(".play")?.addEventListener("click", () => {
      this.hideOverlay();
      this.onRestart();
    });
    this.overlay.querySelector(".share")?.addEventListener("click", () => {
      const text = `I reached enlightenment in Pooping Buddha with ${score} karma 🧘💩 — can you ascend the wheel of samsara?`;
      const nav = navigator as Navigator & { share?: (d: { text: string; title: string }) => Promise<void> };
      if (nav.share) {
        void nav.share({ title: "Pooping Buddha", text });
      } else {
        void navigator.clipboard?.writeText(text);
        const b = this.overlay.querySelector(".share") as HTMLElement;
        b.textContent = "Copied! ✅";
      }
    });
  }

  hideOverlay() {
    this.overlay.className = "overlay";
  }
}
